import { useEffect, useState, type JSX } from 'react'
import type { RefreshJobResult } from '../api'
import { formatElapsed, updatingDetail } from '../autoRefresh'

// What the page says while Refresh runs and after it finishes. The bar is the
// same for an automatic refresh on open and a manual click, so "updating" looks
// like one state; the numbers underneath stay on screen (dimmed by App) until
// the new ones land.

export function UpdatingBar({ startedAt }: { startedAt: number }): JSX.Element {
  const elapsedSince = (): number => Math.floor((Date.now() - startedAt) / 1000)
  const [elapsed, setElapsed] = useState(elapsedSince)
  useEffect(() => {
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000)
    return () => clearInterval(timer)
  }, [startedAt])
  return (
    <div className="updating-bar">
      <div className="spinner small" aria-hidden="true" />
      <div className="updating-text">
        <div className="updating-title">
          Getting the latest numbers from the sheets and freee…
        </div>
        <div className="updating-detail">{updatingDetail(elapsed)}</div>
      </div>
      {/* Hidden from screen readers: a ticking clock would be read out every second. */}
      <span className="updating-elapsed" aria-hidden="true">
        {formatElapsed(elapsed)}
      </span>
    </div>
  )
}

// Same footprint as the bar so nothing jumps when it swaps in. App owns the 4s timer.
export function UpdatedNote(): JSX.Element {
  return (
    <div className="updating-bar updated-note">
      <span className="updated-tick" aria-hidden="true">
        ✓
      </span>
      <span>Updated just now</span>
    </div>
  )
}

// The live region is always mounted, empty when idle, and the bar or the note
// is put INTO it: several screen readers (VoiceOver especially) skip a region
// that arrives already filled, so a bar carrying its own role would go unread.
export function RefreshStatusRegion({
  startedAt,
  justUpdated,
}: {
  startedAt: number | null
  justUpdated: boolean
}): JSX.Element {
  return (
    <div role="status" aria-live="polite">
      {startedAt !== null ? <UpdatingBar startedAt={startedAt} /> : justUpdated && <UpdatedNote />}
    </div>
  )
}

const JOB_LABELS: Record<string, string> = {
  'deal-ledger': 'Deals',
  payroll: 'Payroll',
  'forecast-snapshot': 'Forecast',
  freee: 'Costs (freee)',
}

// The outcome of the last live ingest, one line per source. A failed source is
// called out in words, never hidden behind the successes beside it.
export function RefreshBanner({
  results,
  onDismiss,
}: {
  results: RefreshJobResult[]
  onDismiss: () => void
}): JSX.Element {
  const anyFailed = results.some((r) => r.status !== 'succeeded')
  return (
    <div className={`refresh-banner${anyFailed ? ' has-failure' : ''}`} role="status">
      <div className="refresh-banner-head">
        <strong>{anyFailed ? 'Refresh finished with problems' : 'Refresh complete'}</strong>
        <button type="button" className="link-button" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
      <ul>
        {results.map((r) => (
          <li key={r.job} className={r.status === 'succeeded' ? 'ok' : 'bad'}>
            {JOB_LABELS[r.job] ?? r.job}:{' '}
            {r.status === 'succeeded' ? (
              <>
                {r.rowsRead ?? 0} read
                {r.rowsRejected ? `, ${r.rowsRejected} quarantined` : ''}
              </>
            ) : (
              <>failed — {r.message ?? 'unknown error'}</>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
